import styled from "@emotion/styled";
import {
    DatePicker,
    DateRange,
    DesktopDateRangePicker as DateRangePicker,
    LocalizationProvider,
    PickersDay,
    PickersDayProps,
} from "@mui/lab";
import AdapterDatetime from "@mui/lab/AdapterMoment";
import { Icon, IconButton, InputAdornment, TextField } from "@mui/material";
import { CustomDropdown, LightRadio } from "components/common";
import TimezoneDropdown from "components/common/TimezoneDropdown";
import { ConfirmDownloadModal } from "components/modals/ConfirmDownloadModal";
import { DATE_MONTH_YEAR_FORMAT } from "constant";
import { SensorType } from "constant/gateway";
import { useOrganisation } from "hooks/organization";
import { isEmpty, keys, map, union } from "lodash";
import moment, { Moment } from "moment-timezone";
import { ChangeEvent, memo, useEffect, useRef, useState } from "react";
import { Button, Card, Dropdown, Form, Modal, Row } from "react-bootstrap";
import styledConst from "styles";
import { DownloadIntervalTime, DownloadPayload } from "types";
import { isOverOneYear } from "utils/functions";
import { getSensorType } from "utils/sensorFunctions";
import {
    CalendarViews,
    CustomMonthPicker,
    CustomPickersDay,
} from "./ChartByTime";

interface Props {
    show: boolean;
    panel: any;
    deviceConfig: any[];
    onClose?: () => void;
}

type Device = {
    port: string;
    gatewayName: string;
    name?: string;
    sensorName: string;
    id: string;
    gatewayId: string;
    deviceId: string;
    said: number;
    object: string;
    cls: string;
};

const DownloadModal = styled(Modal)`
    .modal-dialog {
        max-width: 635px;
    }
`;

const Box = styled(Card)`
    border: 2px solid #eaeaea;
`;

const DeviceItem = styled(Card)`
    margin-bottom: 1rem;
    border: 2px solid #eaeaea;

    .card-body {
        display: flex;
        justify-content: space-between;
    }
`;

const DeviceInfo = styled.div`
    color: ${styledConst.Primary_Blue_2};

    .subtext {
        font-size: 0.75rem;
        color: #828fab;
        opacity: 0.75;
    }
`;

const DeviceControl = styled.div`
    align-self: center;
`;

const CheckBox = styled(Form.Check)`
    .custom-control-label {
        color: ${styledConst.Primary_Blue_3};

        &::before {
            background-color: #dddddd;
            border-color: #dddddd;
        }
    }
`;

const RangeTypes = {
    Day: "Day",
    Week: "Week",
    Month: "Month",
    Year: "Year",
    Custom: "Custom",
};

type RangeType = keyof typeof RangeTypes;

type RangePickerProps = {
    rangeType: RangeType;
    value: DateRange<Moment>;
    onChange: (value: [Moment, Moment]) => void;
};

type TimeUnit = moment.unitOfTime.StartOf;

type CustomDateRange = [Moment, Moment];

const RangePicker = ({ rangeType, value, onChange }: RangePickerProps) => {
    const [hoveredDay, setHoveredDay] = useState<Moment | null>(null);
    const [dateRange, setDateRange] = useState<CustomDateRange>([
        moment(),
        moment(),
    ]);
    const [calendarOpen, setCalendarOpen] = useState<boolean>(false);
    const [calendarViews, setCalendarViews] = useState(
        CalendarViews[RangeTypes.Day]
    );
    const startInputRef = useRef<HTMLInputElement>();
    const endInputRef = useRef<HTMLInputElement>();
    const dropdownRef = useRef<HTMLButtonElement | null>(null);
    const pickerId = calendarOpen ? "calendar" : undefined;

    useEffect(() => {
        setCalendarViews(CalendarViews[rangeType]);
    }, [rangeType]);

    useEffect(() => {
        setDateRange(value as CustomDateRange);
    }, [value]);

    const getSelectedLabel = () => {
        const [startDate, endDate] = dateRange;
        const end = endDate.format(DATE_MONTH_YEAR_FORMAT);

        switch (rangeType) {
            case RangeTypes.Week: {
                const start = startDate.format(DATE_MONTH_YEAR_FORMAT);
                return `${start} - ${end}`;
            }
            case RangeTypes.Month: {
                return endDate.format("MMM YYYY");
            }
            case RangeTypes.Year: {
                return endDate.format("YYYY");
            }
            default:
                return end;
        }
    };

    const applySelectedDate = async (value: DateRange<Moment>) => {
        let [startDate, endDate] = value;
        const timeUnit = rangeType.toLowerCase() as TimeUnit;

        if (!endDate || endDate.isSame(rangeType[1], timeUnit)) {
            return;
        }

        endDate = endDate.endOf(timeUnit);
        startDate = (startDate ?? endDate.clone()).startOf(timeUnit);

        onChange([startDate, endDate]);
        setDateRange([startDate, endDate]);
    };

    const onDateRangeChange = (value: DateRange<Moment>) => {
        if (value[0] && value[1]) {
            applySelectedDate(value as CustomDateRange);
        }
    };

    const onOpenCalendar = () => {
        setCalendarOpen(!calendarOpen);
    };

    const renderWeekPickerDay = (
        date: Moment,
        selectedDates: Array<Moment | null>,
        pickersDayProps: PickersDayProps<Moment>
    ) => {
        if (rangeType !== RangeTypes.Week) {
            return <PickersDay {...pickersDayProps} />;
        }

        const [, endDate] = dateRange;

        const start = endDate.clone().startOf("week");
        const end = endDate.clone().endOf("week");

        const isSelected = date.isBetween(start, end, "day", "[]");
        const isHovered = date.isSame(hoveredDay, "week");

        return (
            <CustomPickersDay
                {...pickersDayProps}
                selected={false}
                disableMargin
                isSelected={isSelected}
                isHovered={isHovered}
                onPointerEnter={() => setHoveredDay(date)}
                onPointerLeave={() => setHoveredDay(null)}
            />
        );
    };

    const renderPicker = () => (
        <>
            <Form.Label className="text-primary-blue-3">
                Choose {rangeType}
            </Form.Label>
            <CustomDropdown>
                {rangeType === RangeTypes.Month && (
                    <CustomMonthPicker
                        open={calendarOpen}
                        anchorElement={dropdownRef.current}
                        id={pickerId}
                        disableFuture
                        onChange={(value) => applySelectedDate([null, value])}
                    />
                )}
                {rangeType !== RangeTypes.Month && (
                    <DatePicker
                        showDaysOutsideCurrentMonth
                        open={calendarOpen}
                        onClose={() => setCalendarOpen(false)}
                        views={calendarViews}
                        value={dateRange[1]}
                        onChange={(value) => applySelectedDate([null, value])}
                        minDate={moment("2022-01-01")}
                        renderDay={renderWeekPickerDay}
                        disableFuture
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                style={{ opacity: 0, width: 0, height: 0 }}
                            />
                        )}
                    />
                )}
                <Dropdown.Toggle
                    className="time-range"
                    onClick={onOpenCalendar}
                    ref={dropdownRef}
                >
                    {getSelectedLabel()}
                    <i className="material-icons float-right">expand_more</i>
                </Dropdown.Toggle>
            </CustomDropdown>
        </>
    );

    const renderRangePicker = () => (
        <DateRangePicker
            disableFuture
            startText={""}
            endText={""}
            value={dateRange}
            onChange={onDateRangeChange}
            inputFormat={DATE_MONTH_YEAR_FORMAT}
            InputAdornmentProps={{
                position: "end",
                children: (
                    <IconButton>
                        <i className="material-icons">date_range</i>
                    </IconButton>
                ),
            }}
            minDate={moment("2022-01-01")}
            renderInput={(startProps, endProps) => {
                return (
                    <>
                        <div className={`dateTimeFieldWrapper inline`}>
                            <div className="dateTimeField">From</div>
                            <TextField
                                {...startProps}
                                inputRef={startInputRef}
                                InputLabelProps={{ shrink: false }}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="start-date-adornment"
                                                edge="end"
                                                onClick={(e) => {
                                                    startInputRef.current?.focus();
                                                }}
                                            >
                                                <Icon>event</Icon>
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </div>

                        <div className={`dateTimeFieldWrapper inline`}>
                            <div className="dateTimeField">To</div>
                            <TextField
                                {...endProps}
                                inputRef={endInputRef}
                                InputLabelProps={{ shrink: false }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="end-date-adornment"
                                                edge="end"
                                                onClick={(e) => {
                                                    endInputRef.current?.focus();
                                                }}
                                            >
                                                <Icon>event</Icon>
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </div>
                    </>
                );
            }}
        />
    );

    return (
        <LocalizationProvider dateAdapter={AdapterDatetime}>
            {rangeType !== "Custom" && renderPicker()}
            {rangeType === "Custom" && renderRangePicker()}
        </LocalizationProvider>
    );
};

const DownloadChartModalComponent = (props: Props) => {
    const { defaultTimeZone } = useOrganisation();
    const [showModal, setShowModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [timezone, setTimezone] = useState<string>(defaultTimeZone);
    const [devices, setDevices] = useState<Device[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const { show, panel, deviceConfig, onClose } = props;
    const [rangeType, setRangeType] = useState(RangeTypes.Day as RangeType);
    const [intervalTime, setIntervalTime] =
        useState<DownloadIntervalTime>("Hourly");
    const [dateRange, setDateRange] = useState<CustomDateRange>([
        moment().tz(defaultTimeZone),
        moment().tz(defaultTimeZone),
    ]);
    const [dateError, setDateError] = useState<string>("");
    const disableSubmission =
        (devices?.length > 1 && selectedIds.length === 0) || !!dateError;

    const getSourceName = () => {
        if (isEmpty(devices)) {
            return "";
        }

        const consumptionType =
            getSensorType({ CLS: devices[0].cls }) ===
            SensorType.WaterConsumption
                ? "Water Flow"
                : "Energy";
        return `${consumptionType} Consumption Data of`;
    };

    const getDevicesInfo = async () => {
        const { attributes } = panel;

        const devices = attributes.map((attribute: any, index: number) => {
            const {
                gateway_id: gatewayId,
                device_id: id,
                said,
                name: sensorName,
            } = attribute;

            const {
                PORT: port,
                OBJ,
                sensor_name: deviceName,
                CLS,
                gatewayName,
            } = deviceConfig[index];

            return {
                port,
                gatewayId,
                gatewayName: gatewayName,
                said,
                sensorName,
                name: deviceName,
                id: `${id}_${said}`,
                deviceId: id,
                object: OBJ,
                cls: CLS,
            };
        });

        setDevices(devices);
    };

    const handleSelectDevice = (
        e: ChangeEvent<HTMLInputElement>,
        device: Device | string
    ) => {
        const { checked } = e.target;
        let ids = [...selectedIds];
        const _device = device as Device;

        if (checked) {
            if (device === "all") {
                ids = map(devices, "id");
            } else {
                ids = union(selectedIds, [_device.id]);
            }
        } else if (device === "all") {
            ids = [];
        } else {
            ids = ids.filter((id: string) => id !== _device.id);
        }

        setSelectedIds(ids);
    };

    const handleChangeTimeZone = (timezone: string) => {
        setTimezone(timezone);
    };

    useEffect(() => {
        setShowModal(show);

        if (show) {
            setRangeType(RangeTypes.Day as RangeType);
            setDateRange(
                getCurrentTimeRange(
                    RangeTypes.Day as RangeType
                ) as CustomDateRange
            );
            setSelectedIds([]);
            setTimezone(defaultTimeZone);
            setDateError("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    useEffect(() => {
        setTimezone(defaultTimeZone);
    }, [defaultTimeZone]);

    useEffect(
        () => {
            if (panel && showModal) {
                getDevicesInfo();
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [panel, showModal]
    );

    const getDisplayGatewayName = ({
        port,
        gatewayName,
    }: {
        port: string;
        gatewayName: string;
    }) => {
        return port ? `${gatewayName} - LDSBUS ${port}` : `${gatewayName}`;
    };

    const handleDownloadClick = () => {
        // Validate dates before proceeding
        if (
            !dateRange[0] ||
            !dateRange[1] ||
            !dateRange[0].isValid() ||
            !dateRange[1].isValid()
        ) {
            setDateError("Date and time are required");
            return;
        }

        if (rangeType === RangeTypes.Custom) {
            // Check if the date range is over 1 year
            const isOverYear = isOverOneYear(
                dateRange[0].toDate(),
                dateRange[1].toDate()
            );

            if (isOverYear) {
                setDateError(
                    "You can not select a date range more than 1 year."
                );
                return;
            }

            // Check if the start date is after the end date
            if (dateRange[0].isAfter(dateRange[1])) {
                setDateError("Start date must be before end date");
                return;
            }

            // Check if dates are future dates
            if (
                dateRange[0].isAfter(moment()) ||
                dateRange[1].isAfter(moment())
            ) {
                setDateError("Dates cannot be in the future");
                return;
            }
        }

        setDateError("");
        setShowModal(false);
        setShowConfirmModal(true);
    };

    const handleConfirmClick = async (
        processConfirm: (data: DownloadPayload) => Promise<void> | void
    ) => {
        // Validate that dates are not empty
        if (
            !dateRange[0] ||
            !dateRange[1] ||
            !dateRange[0].isValid() ||
            !dateRange[1].isValid()
        ) {
            // Show error - reopen the modal to show error
            setShowConfirmModal(false);
            setShowModal(true);
            return;
        }

        const _devices = devices
            .filter((d) => selectedIds.includes(d.id) || devices.length === 1)
            .map(({ gatewayId: gateway_id, deviceId: device_id, said }) => ({
                gateway_id,
                device_id,
                said,
            }));
        const params = {
            start: moment
                .tz(
                    dateRange[0].format(DATE_MONTH_YEAR_FORMAT),
                    DATE_MONTH_YEAR_FORMAT,
                    timezone
                )
                .startOf("day")
                .unix(),
            end: moment
                .tz(
                    dateRange[1].format(DATE_MONTH_YEAR_FORMAT),
                    DATE_MONTH_YEAR_FORMAT,
                    timezone
                )
                .add(1, "day")
                .startOf("day")
                .unix(),
            time_zone: timezone,
            attributes: _devices,
            processing: intervalTime,
        };

        await processConfirm(params);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setShowConfirmModal(false);
        onClose && onClose();
    };

    const onRangeTypeChange = (rangeType: RangeType) => {
        setRangeType(rangeType);
        setIntervalTime(rangeType === RangeTypes.Day ? "Hourly" : "Daily");
        setDateRange(getCurrentTimeRange(rangeType) as CustomDateRange);
        setDateError("");
    };

    const getCurrentTimeRange = (rangeType: RangeType) => {
        const timeUnit = rangeType.toLowerCase() as TimeUnit;
        let start = moment().startOf(timeUnit);
        let end = moment().endOf(timeUnit);

        if (rangeType === RangeTypes.Custom) {
            start.subtract(1, "day");
        }

        return [start, end];
    };

    const handleDateTimeRangeChange = (value: DateRange<Moment>) => {
        if (value[0] && value[1]) {
            const isOverYear = isOverOneYear(
                value[0].toDate(),
                value[1].toDate()
            );

            if (isOverYear) {
                setDateError(
                    "You can not select a date range more than 1 year"
                );
            } else {
                setDateError("");
            }

            setDateRange(value as CustomDateRange);
        }
    };

    const onChangeIntervalTime = (e: ChangeEvent<HTMLInputElement>) => {
        setIntervalTime(e.target.value as DownloadIntervalTime);
    };

    return (
        <>
            <DownloadModal
                className=""
                show={showModal}
                onHide={handleCloseModal}
                aria-labelledby="contained-modal-title-vcenter"
                centered
                size="lg"
                scrollable
            >
                <Modal.Header>
                    <Modal.Title id="example-modal-sizes-title-sm">
                        Download Data
                    </Modal.Title>
                    <Button
                        variant=""
                        className="close-button"
                        onClick={handleCloseModal}
                    >
                        <span className="material-icons">close</span>
                    </Button>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <div>
                            <div className="d-flex justify-content-between">
                                <Form.Label className="text-primary-blue-3 mb-3">
                                    {getSourceName()}
                                </Form.Label>
                                {devices.length > 1 && (
                                    <CheckBox
                                        custom
                                        className="pr-1"
                                        type="checkbox"
                                        id="device-all"
                                        label="All"
                                        checked={
                                            selectedIds.length > 0 &&
                                            selectedIds.length ===
                                                devices.length
                                        }
                                        onChange={(e) =>
                                            handleSelectDevice(e, "all")
                                        }
                                    />
                                )}
                            </div>
                            {devices.map((device, index) => (
                                <DeviceItem key={device.id}>
                                    <Card.Body className="py-3">
                                        <DeviceInfo>
                                            <Row className="device-name">
                                                <span
                                                    className={`d-flex ${
                                                        device.name
                                                            ? "mute"
                                                            : "text-secondary-red-1"
                                                    }`}
                                                ></span>
                                                <span aria-label="device_display_name">
                                                    {device.name ??
                                                        `Sensor information not found`}
                                                </span>
                                            </Row>
                                            <Row className="subtext device-ldsu">
                                                {getDisplayGatewayName({
                                                    port: device.port,
                                                    gatewayName:
                                                        device.gatewayName,
                                                })}
                                            </Row>
                                            <Row className="subtext device-id">
                                                LDSU UUID: {device.deviceId}
                                            </Row>
                                            <Row className="subtext sensor-name">
                                                {device.sensorName}
                                            </Row>
                                        </DeviceInfo>
                                        <DeviceControl>
                                            {devices.length > 1 && (
                                                <CheckBox
                                                    custom
                                                    type="checkbox"
                                                    id={`device-${index}`}
                                                    checked={selectedIds.includes(
                                                        device.id
                                                    )}
                                                    onChange={(e) =>
                                                        handleSelectDevice(
                                                            e,
                                                            device
                                                        )
                                                    }
                                                />
                                            )}
                                        </DeviceControl>
                                    </Card.Body>
                                </DeviceItem>
                            ))}
                        </div>
                        <div className="mt-4">
                            <Form.Label className="text-primary-blue-3 mb-2">
                                Time Zone
                            </Form.Label>
                            <Box body>
                                <Form.Label className="text-primary-blue-3">
                                    Select a time zone
                                </Form.Label>
                                <TimezoneDropdown
                                    value={timezone}
                                    onChange={handleChangeTimeZone}
                                />
                            </Box>
                        </div>
                        <div className="mt-3">
                            <Form.Label className="text-primary-blue-3 mb-3">
                                Select Range Type
                            </Form.Label>
                            <Form.Group className="text-primary-blue-3 mb-3">
                                {keys(RangeTypes).map((rType, index) => (
                                    <LightRadio
                                        className={`${
                                            index !== 0 ? "ml-3" : ""
                                        }`}
                                        key={rType}
                                        inline
                                        type="radio"
                                        custom
                                        id={`range-type-${rType}`}
                                        checked={rangeType === rType}
                                        name="range-type"
                                        label={
                                            <span className="ml-1">
                                                {rType}
                                            </span>
                                        }
                                        onChange={() =>
                                            onRangeTypeChange(
                                                rType as RangeType
                                            )
                                        }
                                    />
                                ))}
                            </Form.Group>
                            <Box>
                                <Card.Body
                                    className={`${
                                        rangeType === "Custom"
                                            ? "pt-0 pb-1"
                                            : ""
                                    }`}
                                >
                                    <RangePicker
                                        rangeType={rangeType}
                                        value={dateRange}
                                        onChange={handleDateTimeRangeChange}
                                    />

                                    {dateError && (
                                        <div className="mt-3">
                                            <p
                                                role="alert"
                                                className="text-danger"
                                            >
                                                {dateError}
                                            </p>
                                        </div>
                                    )}
                                </Card.Body>
                            </Box>
                        </div>
                        <div className="mt-3">
                            <Form.Label className="text-primary-blue-3 mb-2">
                                Select Consumption Interval
                            </Form.Label>
                            <Form.Group className="text-primary-blue-3 mb-3">
                                {rangeType !== RangeTypes.Day && (
                                    <LightRadio
                                        value="Daily"
                                        inline
                                        type="radio"
                                        custom
                                        id={`interval-daily`}
                                        checked={intervalTime === "Daily"}
                                        name="interval"
                                        label={
                                            <span className="ml-1">Daily</span>
                                        }
                                        onChange={onChangeIntervalTime}
                                    />
                                )}
                                <LightRadio
                                    className={`${
                                        rangeType !== RangeTypes.Day
                                            ? "ml-3"
                                            : ""
                                    }`}
                                    value="Hourly"
                                    inline
                                    type="radio"
                                    custom
                                    id={`interval-hourly`}
                                    checked={intervalTime === "Hourly"}
                                    name="interval"
                                    label={<span className="ml-1">Hourly</span>}
                                    onChange={onChangeIntervalTime}
                                />
                            </Form.Group>
                        </div>
                        <div className="mt-3">
                            <Form.Label className="text-primary-blue-3 mb-2">
                                Download Options
                            </Form.Label>
                            <Form.Group className="text-primary-blue-3 mb-3">
                                <LightRadio
                                    value="fast"
                                    inline
                                    type="radio"
                                    custom
                                    id={`download-fast`}
                                    defaultChecked={true}
                                    name="download-option"
                                    label={
                                        <span className="ml-1">
                                            Fast Download (Ranges up to 1 year)
                                        </span>
                                    }
                                />
                            </Form.Group>
                        </div>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="btn-create float-right"
                        onClick={handleDownloadClick}
                        disabled={disableSubmission}
                    >
                        Download
                    </Button>
                </Modal.Footer>
            </DownloadModal>
            <ConfirmDownloadModal
                show={showConfirmModal}
                onClose={handleCloseModal}
                downloadType={"fast"}
                onConfirm={handleConfirmClick}
            />
        </>
    );
};

export const DownloadConsumptionChartModal = memo(DownloadChartModalComponent);
